import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import TodayAttendanceCard from "components/attendance/TodayAttendanceCard";

import {
    getDashboard,
} from "services/api/dashboardApi";

import {
    showError,
} from "utils/toast";

import {
    FiArrowLeft,
    FiArrowUpRight,
    FiBarChart2,
    FiCalendar,
    FiCheckCircle,
    FiClock,
    FiDollarSign,
    FiMapPin,
    FiPackage,
    FiRefreshCw,
    FiTarget,
    FiUsers,
} from "react-icons/fi";

import {
    ResponsiveContainer,
    LineChart,
    Line,
    BarChart,
    Bar,
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    PieChart,
    Pie,
    Cell,
    ComposedChart,
} from "recharts";


/*
|--------------------------------------------------------------------------
| CONSTANTS
|--------------------------------------------------------------------------
*/

const CHART_COLORS = {
    indigo: "#4F46E5",
    blue: "#2563EB",
    green: "#16A34A",
    orange: "#EA580C",
    purple: "#7C3AED",
    teal: "#0D9488",
    red: "#DC2626",
    gray: "#94A3B8",
};


const KPI_CONFIG = [
    {
        key: "sales",
        title: "Active Sales Team",
        subtitle: "Field sales personnel",
        color: "indigo",
        icon: FiUsers,
    },
    {
        key: "visits",
        title: "Field Visits",
        subtitle: "Visits in selected period",
        color: "blue",
        icon: FiMapPin,
    },
    {
        key: "collections",
        title: "Collections",
        subtitle: "Verified collection value",
        color: "green",
        icon: FiDollarSign,
    },
    {
        key: "pipeline",
        title: "Sales Pipeline",
        subtitle: "Expected order value",
        color: "purple",
        icon: FiTarget,
    },
    {
        key: "approvals",
        title: "Pending Approvals",
        subtitle: "Management attention",
        color: "orange",
        icon: FiClock,
    },
    {
        key: "attendance",
        title: "Attendance",
        subtitle: "Team compliance",
        color: "teal",
        icon: FiCheckCircle,
    },
];


const COLOR_MAP = {
    indigo: {
        border: "border-indigo-100",
        bg: "bg-indigo-50",
        icon: "bg-indigo-100 text-indigo-600",
        value: "text-indigo-700",
        line: "bg-indigo-500",
    },

    blue: {
        border: "border-blue-100",
        bg: "bg-blue-50",
        icon: "bg-blue-100 text-blue-600",
        value: "text-blue-700",
        line: "bg-blue-500",
    },

    green: {
        border: "border-green-100",
        bg: "bg-green-50",
        icon: "bg-green-100 text-green-600",
        value: "text-green-700",
        line: "bg-green-500",
    },

    purple: {
        border: "border-purple-100",
        bg: "bg-purple-50",
        icon: "bg-purple-100 text-purple-600",
        value: "text-purple-700",
        line: "bg-purple-500",
    },

    orange: {
        border: "border-orange-100",
        bg: "bg-orange-50",
        icon: "bg-orange-100 text-orange-600",
        value: "text-orange-700",
        line: "bg-orange-500",
    },

    teal: {
        border: "border-teal-100",
        bg: "bg-teal-50",
        icon: "bg-teal-100 text-teal-600",
        value: "text-teal-700",
        line: "bg-teal-500",
    },
};


/*
|--------------------------------------------------------------------------
| DATE HELPERS
|--------------------------------------------------------------------------
*/

const formatDateInput = (date) => {
    const year = date.getFullYear();

    const month = String(
        date.getMonth() + 1
    ).padStart(2, "0");

    const day = String(
        date.getDate()
    ).padStart(2, "0");

    return `${year}-${month}-${day}`;
};


const getPresetRange = (preset) => {

    const today = new Date();

    const start = new Date(today);
    const end = new Date(today);


    switch (preset) {

        case "yesterday":

            start.setDate(
                today.getDate() - 1
            );

            end.setDate(
                today.getDate() - 1
            );

            break;


        case "week": {

            const day = today.getDay();

            const diff =
                day === 0
                    ? 6
                    : day - 1;

            start.setDate(
                today.getDate() - diff
            );

            break;
        }


        case "last7":

            start.setDate(
                today.getDate() - 6
            );

            break;


        case "lastMonth":

            start.setDate(1);

            start.setMonth(
                today.getMonth() - 1
            );

            end.setDate(0);

            break;


        case "quarter": {

            const quarter =
                Math.floor(
                    today.getMonth() / 3
                );

            start.setMonth(
                quarter * 3,
                1
            );

            break;
        }


        case "year":

            start.setMonth(
                0,
                1
            );

            break;


        case "month":

            start.setDate(1);

            break;


        case "today":
        default:

            break;
    }


    return {
        fromDate:
            formatDateInput(start),

        toDate:
            formatDateInput(end),
    };
};


/*
|--------------------------------------------------------------------------
| NUMBER FORMATTERS
|--------------------------------------------------------------------------
*/

const formatCurrency = (
    value = 0
) => {

    return `₹${Number(
        value || 0
    ).toLocaleString(
        "en-IN"
    )}`;
};


const formatNumber = (
    value = 0
) => {

    return Number(
        value || 0
    ).toLocaleString(
        "en-IN"
    );
};




/*
|--------------------------------------------------------------------------
| LOGGED-IN USER / ROLE HELPERS
|--------------------------------------------------------------------------
|
| IMPORTANT:
| Do not decide dashboard type from the API response.
| The backend already decides what data the user is allowed to see.
| The frontend only uses the logged-in role to decide which existing
| dashboard UI should be rendered.
|
| ADMIN / SUPER ADMIN -> existing full dashboard
| EVERY OTHER ROLE    -> attendance only
|--------------------------------------------------------------------------
*/

const getLoggedInUser = () => {
    try {
        const rawUser =
            localStorage.getItem("user");

        if (!rawUser) {
            return null;
        }

        return JSON.parse(rawUser);
    } catch (error) {
        console.error(
            "Unable to read logged-in user:",
            error
        );

        return null;
    }
};


const normalizeRole = (role) => {
    if (!role) {
        return "";
    }

    if (typeof role === "string") {
        return role
            .trim()
            .toLowerCase()
            .replace(/[\s-]+/g, "_");
    }

    return String(
        role?.name ||
        role?.slug ||
        role?.role ||
        role?.code ||
        ""
    )
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, "_");
};


const getLoggedInUserRoles = () => {
    const user =
        getLoggedInUser();

    if (!user) {
        return [];
    }

    const roles = [];

    if (Array.isArray(user.roles)) {
        roles.push(...user.roles);
    }

    if (user.role) {
        roles.push(user.role);
    }

    if (Array.isArray(user?.user?.roles)) {
        roles.push(...user.user.roles);
    }

    if (user?.user?.role) {
        roles.push(user.user.role);
    }

    return roles
        .map(normalizeRole)
        .filter(Boolean);
};


const isAdminUser = () => {
    const roles =
        getLoggedInUserRoles();

    return (
        roles.includes("admin") ||
        roles.includes("super_admin")
    );
};


/*
|--------------------------------------------------------------------------
| DASHBOARD
|--------------------------------------------------------------------------
*/

const Dashboard = () => {

    /*
    |--------------------------------------------------------------------------
    | Default = This Month
    |--------------------------------------------------------------------------
    */

    const initialRange =
        getPresetRange("month");


    const [
        dashboard,
        setDashboard,
    ] = useState(null);

    const [
        isAdminDashboard,
        setIsAdminDashboard,
    ] = useState(() => isAdminUser());


    const [
        loading,
        setLoading,
    ] = useState(true);


    const [
        refreshing,
        setRefreshing,
    ] = useState(false);


    const [
        activeSection,
        setActiveSection,
    ] = useState(() =>
        isAdminUser()
            ? "overview"
            : "attendance"
    );


    const [
        preset,
        setPreset,
    ] = useState("month");


    const [
        fromDate,
        setFromDate,
    ] = useState(
        initialRange.fromDate
    );


    const [
        toDate,
        setToDate,
    ] = useState(
        initialRange.toDate
    );


    /*
    |--------------------------------------------------------------------------
    | API
    |--------------------------------------------------------------------------
    */

    const loadDashboard =
    useCallback(
        async (
            range = {
                fromDate,
                toDate,
            }
        ) => {

            try {

                setRefreshing(true);

                const response =
                    await getDashboard({
                        fromDate:
                            range.fromDate,

                        toDate:
                            range.toDate,
                    });

                const result =
                    response?.data ||
                    response;

                const dashboardData =
                    result?.data ||
                    null;

                setDashboard(
                    dashboardData
                );

                /*
                |--------------------------------------------------------------------------
                | DASHBOARD ACCESS
                |--------------------------------------------------------------------------
                |
                | IMPORTANT:
                | Never infer the user's role from the API response.
                | The backend is already role-aware.
                |
                | Admin / Super Admin -> existing full dashboard
                | Other roles         -> attendance only
                |--------------------------------------------------------------------------
                */

                const adminDashboard =
                    isAdminUser();

                setIsAdminDashboard(
                    adminDashboard
                );

                setActiveSection(
                    adminDashboard
                        ? "overview"
                        : "attendance"
                );

            } catch (
                error
            ) {

                console.error(
                    "Dashboard error:",
                    error
                );

                // Keep the existing admin error behaviour.
                // For non-admin users the dashboard is attendance-only, so
                // do not show an analytics error toast if the role-limited
                // dashboard response is unavailable. The existing
                // TodayAttendanceCard continues to provide attendance actions.
                if (isAdminUser()) {
                    showError(
                        error?.response?.data?.message ||
                        "Unable to load dashboard."
                    );
                }

            } finally {

                setLoading(false);
                setRefreshing(false);

            }
        },
        [
            fromDate,
            toDate,
        ]
    );


    /*
    |--------------------------------------------------------------------------
    | INITIAL LOAD
    |--------------------------------------------------------------------------
    | Keep the existing API flow. This only ensures the dashboard is loaded
    | once when the page opens.
    |--------------------------------------------------------------------------
    */

    useEffect(() => {
        loadDashboard({
            fromDate: initialRange.fromDate,
            toDate: initialRange.toDate,
        });
        // Initial page load only. Date filters call loadDashboard directly.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    /*
    |--------------------------------------------------------------------------
    | FILTER
    |--------------------------------------------------------------------------
    */

    const handlePresetChange =
        (event) => {

            const selected =
                event.target.value;


            setPreset(
                selected
            );


            if (
                selected ===
                "custom"
            ) {
                return;
            }


            const range =
                getPresetRange(
                    selected
                );


            setFromDate(
                range.fromDate
            );

            setToDate(
                range.toDate
            );


            loadDashboard(
                range
            );
        };


    const applyCustomDate =
        () => {

            loadDashboard({
                fromDate,
                toDate,
            });
        };


    /*
    |--------------------------------------------------------------------------
    | KPI VALUES
    |--------------------------------------------------------------------------
    */

    const kpis =
        dashboard?.kpis ||
        {};


    const activeSalesPeople =
        kpis.activeSalesPeople ??
        kpis.activeEmployees ??
        0;


    const totalVisits =
        dashboard?.visits?.total ||
        0;


    const totalCollection =
        dashboard?.collections?.amount ||
        kpis.collectionAmount ||
        0;


    const expectedOrder =
        dashboard?.appraisals?.expectedOrder ||
        kpis.expectedOrders ||
        0;


    const pendingApprovals =
        kpis.pendingApprovals ||
        0;


    const attendanceCompliance =
        kpis.attendanceCompliance ||
        0;


    /*
    |--------------------------------------------------------------------------
    | KPI NAVIGATION
    |--------------------------------------------------------------------------
    */

    const handleKpiClick =
        (key) => {

            const sectionMap = {
                sales:
                    "sales",

                visits:
                    "visits",

                collections:
                    "collections",

                pipeline:
                    "appraisals",

                approvals:
                    "approvals",

                attendance:
                    "attendance",
            };


            setActiveSection(
                sectionMap[key] ||
                "overview"
            );
        };


    /*
    |--------------------------------------------------------------------------
    | BACK
    |--------------------------------------------------------------------------
    */

    const goToOverview =
        () => {

            setActiveSection(
                "overview"
            );
        };


    /*
    |--------------------------------------------------------------------------
    | LOADING
    |--------------------------------------------------------------------------
    */

    if (
        loading &&
        !dashboard
    ) {

        return (
            <div
                className="
                    mt-6
                    flex
                    min-h-[600px]
                    items-center
                    justify-center
                "
            >

                <div className="text-center">

                    <div
                        className="
                            mx-auto
                            mb-4
                            h-10
                            w-10
                            animate-spin
                            rounded-full
                            border-4
                            border-gray-200
                            border-t-brand-500
                        "
                    />

                    <p
                        className="
                            text-sm
                            font-medium
                            text-gray-500
                        "
                    >
                        Loading CRM analytics...
                    </p>

                </div>

            </div>
        );
    }


    /*
    |--------------------------------------------------------------------------
    | PAGE
    |--------------------------------------------------------------------------
    */

    return (

        <div
            className="
                mt-5
                space-y-5
                pb-10
            "
        >

            {/* ========================================================
                HEADER
            ======================================================== */}

            <div
                className="
                    rounded-2xl
                    border
                    border-gray-200
                    bg-white
                    px-6
                    py-5
                    shadow-sm
                "
            >

                <div
                    className="
                        flex
                        flex-col
                        gap-5
                        xl:flex-row
                        xl:items-center
                        xl:justify-between
                    "
                >

                    <div>

                        {activeSection !== "overview" && (

                            <button
                                type="button"
                                onClick={
                                    goToOverview
                                }
                                className="
                                    mb-2
                                    flex
                                    items-center
                                    gap-2
                                    text-sm
                                    font-semibold
                                    text-brand-500
                                    hover:text-brand-600
                                "
                            >
                                <FiArrowLeft />
                                Back to Dashboard
                            </button>

                        )}


                        <div
                            className="
                                flex
                                items-center
                                gap-3
                            "
                        >

                            <div
                                className="
                                    flex
                                    h-11
                                    w-11
                                    items-center
                                    justify-center
                                    rounded-xl
                                    bg-indigo-50
                                    text-indigo-600
                                "
                            >
                                <FiBarChart2
                                    size={22}
                                />
                            </div>


                            <div>

                                <h1
                                    className="
                                        text-2xl
                                        font-bold
                                        text-navy-700
                                    "
                                >
                                    {!isAdminDashboard
                                    ? "Attendance"
                                    : activeSection === "overview"
                                        ? "CRM Dashboard"
                                        : getSectionTitle(
                                            activeSection
                                    )}
                                </h1>

                                <p
                                    className="
                                        mt-1
                                        text-sm
                                        text-gray-500
                                    "
                                >
                                    {!isAdminDashboard
                                        ? "View your attendance and compliance details."
                                        : activeSection === "overview"
                                            ? "Executive view of field sales, collections, pipeline and team performance."
                                            : "Detailed analytics for the selected business area."
                                    }
                                </p>

                            </div>

                        </div>

                    </div>


                    {/* DATE FILTER */}
                    {isAdminDashboard && (
                    <div
                        className="
                            flex
                            flex-col
                            gap-3
                            sm:flex-row
                            sm:items-end
                        "
                    >

                        <div>

                            <label
                                className="
                                    mb-1
                                    block
                                    text-[11px]
                                    font-bold
                                    uppercase
                                    tracking-wide
                                    text-gray-400
                                "
                            >
                                Period
                            </label>

                            <select
                                value={
                                    preset
                                }
                                onChange={
                                    handlePresetChange
                                }
                                className="
                                    h-10
                                    min-w-[160px]
                                    rounded-lg
                                    border
                                    border-gray-200
                                    bg-gray-50
                                    px-3
                                    text-sm
                                    font-semibold
                                    text-gray-700
                                    outline-none
                                    focus:border-indigo-400
                                    focus:ring-2
                                    focus:ring-indigo-100
                                "
                            >

                                <option value="today">
                                    Today
                                </option>

                                <option value="yesterday">
                                    Yesterday
                                </option>

                                <option value="week">
                                    This Week
                                </option>

                                <option value="last7">
                                    Last 7 Days
                                </option>

                                <option value="month">
                                    This Month
                                </option>

                                <option value="lastMonth">
                                    Last Month
                                </option>

                                <option value="quarter">
                                    This Quarter
                                </option>

                                <option value="year">
                                    This Year
                                </option>

                                <option value="custom">
                                    Custom Range
                                </option>

                            </select>

                        </div>


                        <div>

                            <label
                                className="
                                    mb-1
                                    block
                                    text-[11px]
                                    font-bold
                                    uppercase
                                    tracking-wide
                                    text-gray-400
                                "
                            >
                                From
                            </label>

                            <input
                                type="date"
                                value={
                                    fromDate
                                }
                                onChange={
                                    (event) => {

                                        setPreset(
                                            "custom"
                                        );

                                        setFromDate(
                                            event.target.value
                                        );

                                    }
                                }
                                className="
                                    h-10
                                    rounded-lg
                                    border
                                    border-gray-200
                                    bg-gray-50
                                    px-3
                                    text-sm
                                    font-medium
                                    outline-none
                                    focus:border-indigo-400
                                    focus:ring-2
                                    focus:ring-indigo-100
                                "
                            />

                        </div>


                        <div>

                            <label
                                className="
                                    mb-1
                                    block
                                    text-[11px]
                                    font-bold
                                    uppercase
                                    tracking-wide
                                    text-gray-400
                                "
                            >
                                To
                            </label>

                            <input
                                type="date"
                                value={
                                    toDate
                                }
                                onChange={
                                    (event) => {

                                        setPreset(
                                            "custom"
                                        );

                                        setToDate(
                                            event.target.value
                                        );

                                    }
                                }
                                className="
                                    h-10
                                    rounded-lg
                                    border
                                    border-gray-200
                                    bg-gray-50
                                    px-3
                                    text-sm
                                    font-medium
                                    outline-none
                                    focus:border-indigo-400
                                    focus:ring-2
                                    focus:ring-indigo-100
                                "
                            />

                        </div>


                        <button
                            type="button"
                            onClick={
                                applyCustomDate
                            }
                            className="
                                flex
                                h-10
                                items-center
                                justify-center
                                gap-2
                                rounded-lg
                                bg-indigo-600
                                px-4
                                text-sm
                                font-semibold
                                text-white
                                transition
                                hover:bg-indigo-700
                            "
                        >
                            <FiRefreshCw
                                className={
                                    refreshing
                                        ? "animate-spin"
                                        : ""
                                }
                            />

                            Refresh
                        </button>

                    </div>
                    )}
                </div>

            </div>


            {/* ========================================================
                ATTENDANCE-ONLY DASHBOARD
            ========================================================
            | Sales / other roles see ONLY attendance here.
            | Existing admin dashboard UI below is untouched.
            */}

            {!isAdminDashboard && (
                <>

                    {/* ATTENDANCE KPI ONLY */}
                    <div
                        className="
                            grid
                            grid-cols-1
                            gap-4
                            sm:grid-cols-2
                            xl:grid-cols-6
                        "
                    >
                        <button
                            type="button"
                            onClick={() =>
                                setActiveSection(
                                    "attendance"
                                )
                            }
                            className="
                                group
                                relative
                                overflow-hidden
                                rounded-2xl
                                border
                                border-gray-200
                                bg-white
                                p-5
                                text-left
                                shadow-sm
                                transition
                                duration-200
                                hover:-translate-y-1
                                hover:shadow-lg
                                xl:col-span-1
                            "
                        >
                            <div
                                className="
                                    absolute
                                    left-0
                                    top-0
                                    h-full
                                    w-1
                                    bg-teal-500
                                "
                            />

                            <div
                                className="
                                    flex
                                    items-start
                                    justify-between
                                    gap-3
                                "
                            >
                                <div>
                                    <p
                                        className="
                                            text-[11px]
                                            font-bold
                                            uppercase
                                            tracking-widest
                                            text-gray-400
                                        "
                                    >
                                        Attendance
                                    </p>

                                    <p
                                        className="
                                            mt-3
                                            text-2xl
                                            font-bold
                                            text-teal-700
                                        "
                                    >
                                        {attendanceCompliance}%
                                    </p>

                                    <p
                                        className="
                                            mt-1
                                            text-xs
                                            text-gray-500
                                        "
                                    >
                                        {dashboard?.attendance?.present || 0} present
                                    </p>
                                </div>

                                <div
                                    className="
                                        flex
                                        h-11
                                        w-11
                                        items-center
                                        justify-center
                                        rounded-xl
                                        bg-teal-100
                                        text-teal-600
                                    "
                                >
                                    <FiCheckCircle size={20} />
                                </div>
                            </div>

                            <div
                                className="
                                    mt-4
                                    flex
                                    items-center
                                    justify-between
                                    text-xs
                                    font-semibold
                                    text-gray-400
                                "
                            >
                                <span>
                                    View attendance
                                </span>

                                <FiArrowUpRight />
                            </div>
                        </button>
                    </div>

                    {/* ATTENDANCE ACTION */}
                    <div
                        className="
                            rounded-2xl
                            border
                            border-gray-200
                            bg-white
                            p-5
                            shadow-sm
                        "
                    >
                        <div className="mb-4">
                            <h2
                                className="
                                    text-lg
                                    font-bold
                                    text-navy-700
                                "
                            >
                                Today's Attendance
                            </h2>

                            <p
                                className="
                                    mt-1
                                    text-xs
                                    text-gray-500
                                "
                            >
                                Quick field attendance action
                            </p>
                        </div>

                        <TodayAttendanceCard />
                    </div>

                </>
            )}


                        {/* ========================================================
                OVERVIEW
            ======================================================== */}

            {isAdminDashboard && activeSection === "overview" && (

                <>

                    {/* KPI CARDS */}

                    <div
                        className="
                            grid
                            grid-cols-1
                            gap-4
                            sm:grid-cols-2
                            xl:grid-cols-6
                        "
                    >

                        {KPI_CONFIG.map(
                            (item) => {

                                const Icon =
                                    item.icon;

                                const color =
                                    COLOR_MAP[
                                        item.color
                                    ];


                                let value =
                                    0;


                                let subtitle =
                                    item.subtitle;


                                if (
                                    item.key ===
                                    "sales"
                                ) {

                                    value =
                                        activeSalesPeople;

                                }


                                if (
                                    item.key ===
                                    "visits"
                                ) {

                                    value =
                                        formatNumber(
                                            totalVisits
                                        );

                                    subtitle =
                                        `${dashboard?.visits?.approved || 0} approved`;

                                }


                                if (
                                    item.key ===
                                    "collections"
                                ) {

                                    value =
                                        formatCurrency(
                                            totalCollection
                                        );

                                    subtitle =
                                        `${formatCurrency(
                                            dashboard?.collections?.todayAmount
                                        )} today`;

                                }


                                if (
                                    item.key ===
                                    "pipeline"
                                ) {

                                    value =
                                        formatCurrency(
                                            expectedOrder
                                        );

                                    subtitle =
                                        `${dashboard?.appraisals?.total || 0} appraisals`;

                                }


                                if (
                                    item.key ===
                                    "approvals"
                                ) {

                                    value =
                                        pendingApprovals;

                                    subtitle =
                                        pendingApprovals > 0
                                            ? "Requires action"
                                            : "All clear";

                                }


                                if (
                                    item.key ===
                                    "attendance"
                                ) {

                                    value =
                                        `${attendanceCompliance}%`;

                                    subtitle =
                                        `${dashboard?.attendance?.present || 0} present`;

                                }


                                return (

                                    <button
                                        type="button"
                                        key={
                                            item.key
                                        }
                                        onClick={() =>
                                            handleKpiClick(
                                                item.key
                                            )
                                        }
                                        className="
                                            group
                                            relative
                                            overflow-hidden
                                            rounded-2xl
                                            border
                                            border-gray-200
                                            bg-white
                                            p-5
                                            text-left
                                            shadow-sm
                                            transition
                                            duration-200
                                            hover:-translate-y-1
                                            hover:shadow-lg
                                        "
                                    >

                                        <div
                                            className={`
                                                absolute
                                                left-0
                                                top-0
                                                h-full
                                                w-1
                                                ${color.line}
                                            `}
                                        />


                                        <div
                                            className="
                                                flex
                                                items-start
                                                justify-between
                                                gap-3
                                            "
                                        >

                                            <div>

                                                <p
                                                    className="
                                                        text-[11px]
                                                        font-bold
                                                        uppercase
                                                        tracking-widest
                                                        text-gray-400
                                                    "
                                                >
                                                    {
                                                        item.title
                                                    }
                                                </p>


                                                <p
                                                    className={`
                                                        mt-3
                                                        text-2xl
                                                        font-bold
                                                        ${color.value}
                                                    `}
                                                >
                                                    {
                                                        value
                                                    }
                                                </p>


                                                <p
                                                    className="
                                                        mt-1
                                                        text-xs
                                                        text-gray-500
                                                    "
                                                >
                                                    {
                                                        subtitle
                                                    }
                                                </p>

                                            </div>


                                            <div
                                                className={`
                                                    flex
                                                    h-11
                                                    w-11
                                                    items-center
                                                    justify-center
                                                    rounded-xl
                                                    ${color.icon}
                                                `}
                                            >
                                                <Icon
                                                    size={20}
                                                />
                                            </div>

                                        </div>


                                        <div
                                            className="
                                                mt-4
                                                flex
                                                items-center
                                                justify-between
                                                text-xs
                                                font-semibold
                                                text-gray-400
                                            "
                                        >
                                            <span>
                                                View analytics
                                            </span>

                                            <FiArrowUpRight
                                                className="
                                                    transition
                                                    group-hover:translate-x-0.5
                                                    group-hover:-translate-y-0.5
                                                "
                                            />
                                        </div>

                                    </button>
                                );
                            }
                        )}

                    </div>


                    {/* ATTENDANCE ACTION */}

                    <div
                        className="
                            rounded-2xl
                            border
                            border-gray-200
                            bg-white
                            p-5
                            shadow-sm
                        "
                    >

                        <div className="mb-4">

                            <h2
                                className="
                                    text-lg
                                    font-bold
                                    text-navy-700
                                "
                            >
                                Today's Attendance
                            </h2>

                            <p
                                className="
                                    mt-1
                                    text-xs
                                    text-gray-500
                                "
                            >
                                Quick field attendance action
                            </p>

                        </div>


                        <TodayAttendanceCard />

                    </div>


                    {/* FIRST ROW */}

                    <div
                        className="
                            grid
                            grid-cols-1
                            gap-5
                            xl:grid-cols-8
                        "
                    >

                        <div
                            className="
                                xl:col-span-5
                            "
                        >

                            <SalesVisitTrend
                                data={
                                    dashboard?.visits?.trend ||
                                    []
                                }
                                onClick={() =>
                                    setActiveSection(
                                        "visits"
                                    )
                                }
                            />

                        </div>


                        <div
                            className="
                                xl:col-span-3
                            "
                        >

                            <AttendanceComplianceWidget
                                data={
                                    dashboard?.attendance ||
                                    {}
                                }
                                compliance={
                                    dashboard?.compliance ||
                                    {}
                                }
                                onClick={() =>
                                    setActiveSection(
                                        "attendance"
                                    )
                                }
                            />

                        </div>

                    </div>


                    {/* SECOND ROW */}

                    <div
                        className="
                            grid
                            grid-cols-1
                            gap-5
                            xl:grid-cols-8
                        "
                    >

                        <div
                            className="
                                xl:col-span-5
                            "
                        >

                            <CollectionPerformanceChart
                                data={
                                    dashboard?.collections?.trend ||
                                    []
                                }
                                onClick={() =>
                                    setActiveSection(
                                        "collections"
                                    )
                                }
                            />

                        </div>


                        <div
                            className="
                                xl:col-span-3
                            "
                        >

                            <VisitStatusWidget
                                data={
                                    dashboard?.visits ||
                                    {}
                                }
                                onClick={() =>
                                    setActiveSection(
                                        "visits"
                                    )
                                }
                            />

                        </div>

                    </div>


                    {/* THIRD ROW */}

                    <SalesRankingChart
                        data={
                            dashboard?.salesPerformance ||
                            []
                        }
                        onClick={() =>
                            setActiveSection(
                                "sales"
                            )
                        }
                    />


                    {/* FOURTH ROW */}

                    <div
                        className="
                            grid
                            grid-cols-1
                            gap-5
                            xl:grid-cols-3
                        "
                    >

                        <AppraisalWidget
                            data={
                                dashboard?.appraisals ||
                                {}
                            }
                            onClick={() =>
                                setActiveSection(
                                    "appraisals"
                                )
                            }
                        />


                        <SampleWidget
                            data={
                                dashboard?.samples ||
                                {}
                            }
                            onClick={() =>
                                setActiveSection(
                                    "samples"
                                )
                            }
                        />


                        <ManagementAttention
                            data={
                                dashboard?.alerts ||
                                []
                            }
                            onClick={() =>
                                setActiveSection(
                                    "approvals"
                                )
                            }
                        />

                    </div>

                </>

            )}


            {/* ========================================================
                ANALYTICS SECTIONS
            ======================================================== */}

            {isAdminDashboard && activeSection === "sales" && (

                <SalesAnalytics
                    data={
                        dashboard
                    }
                />

            )}


            {isAdminDashboard && activeSection === "visits" && (

                <VisitAnalytics
                    data={
                        dashboard
                    }
                />

            )}


            {isAdminDashboard && activeSection === "collections" && (

                <CollectionAnalytics
                    data={
                        dashboard
                    }
                />

            )}


            {activeSection === "attendance" && (

                <AttendanceAnalytics
                    data={
                        dashboard
                    }
                />

            )}


            {isAdminDashboard && activeSection === "appraisals" && (

                <AppraisalAnalytics
                    data={
                        dashboard
                    }
                />

            )}


            {isAdminDashboard && activeSection === "samples" && (

                <SampleAnalytics
                    data={
                        dashboard
                    }
                />

            )}


            {isAdminDashboard && activeSection === "approvals" && (

                <ApprovalAnalytics
                    data={
                        dashboard
                    }
                />

            )}

        </div>

    );
};


/*
|--------------------------------------------------------------------------
| SECTION TITLE
|--------------------------------------------------------------------------
*/

const getSectionTitle = (
    section
) => {

    const titles = {

        sales:
            "Sales Performance Analytics",

        visits:
            "Visit Analytics",

        collections:
            "Collection Analytics",

        attendance:
            "Attendance & Compliance",

        appraisals:
            "Appraisal Pipeline",

        samples:
            "Sample Conversion Analytics",

        approvals:
            "Management Approval Queue",
    };


    return (
        titles[section] ||
        "CRM Analytics"
    );
};


/*
|--------------------------------------------------------------------------
| GENERIC CARD
|--------------------------------------------------------------------------
*/

const AnalyticsCard = ({
    title,
    subtitle,
    children,
    className = "",
}) => {

    return (

        <div
            className={`
                rounded-2xl
                border
                border-gray-200
                bg-white
                p-6
                shadow-sm
                ${className}
            `}
        >

            <div className="mb-5">

                <h3
                    className="
                        text-lg
                        font-bold
                        text-navy-700
                    "
                >
                    {title}
                </h3>

                {subtitle && (
                    <p
                        className="
                            mt-1
                            text-xs
                            text-gray-500
                        "
                    >
                        {subtitle}
                    </p>
                )}

            </div>

            {children}

        </div>
    );
};


/*
|--------------------------------------------------------------------------
| SALES + VISIT TREND
|--------------------------------------------------------------------------
*/

const SalesVisitTrend = ({
    data = [],
    onClick,
}) => {

    return (

        <AnalyticsCard
            title="Sales & Visit Activity"
            subtitle="Field execution across the selected period"
        >

            <div className="h-[340px]">

                {data.length === 0 ? (

                    <EmptyState />

                ) : (

                    <ResponsiveContainer
                        width="100%"
                        height="100%"
                    >

                        <LineChart
                            data={
                                data
                            }
                            onClick={
                                onClick
                            }
                            margin={{
                                top: 10,
                                right: 10,
                                left: -20,
                                bottom: 0,
                            }}
                        >

                            <CartesianGrid
                                strokeDasharray="3 3"
                                vertical={false}
                                stroke="#E5E7EB"
                            />

                            <XAxis
                                dataKey="label"
                                tick={{
                                    fontSize: 11,
                                }}
                                axisLine={false}
                                tickLine={false}
                            />

                            <YAxis
                                allowDecimals={false}
                                axisLine={false}
                                tickLine={false}
                                tick={{
                                    fontSize: 11,
                                }}
                            />

                            <Tooltip />

                            <Legend />

                            <Line
                                type="monotone"
                                dataKey="planned"
                                stroke={
                                    CHART_COLORS.indigo
                                }
                                strokeWidth={3}
                                dot={false}
                                name="Planned"
                            />

                            <Line
                                type="monotone"
                                dataKey="started"
                                stroke={
                                    CHART_COLORS.blue
                                }
                                strokeWidth={3}
                                dot={false}
                                name="Started"
                            />

                            <Line
                                type="monotone"
                                dataKey="submitted"
                                stroke={
                                    CHART_COLORS.orange
                                }
                                strokeWidth={3}
                                dot={false}
                                name="Submitted"
                            />

                            <Line
                                type="monotone"
                                dataKey="approved"
                                stroke={
                                    CHART_COLORS.green
                                }
                                strokeWidth={3}
                                dot={false}
                                name="Approved"
                            />

                        </LineChart>

                    </ResponsiveContainer>

                )}

            </div>

        </AnalyticsCard>
    );
};


/*
|--------------------------------------------------------------------------
| COLLECTION PERFORMANCE
|--------------------------------------------------------------------------
*/

const CollectionPerformanceChart = ({
    data = [],
    onClick,
}) => {

    const chartData =
        data.map(
            (item) => ({
                ...item,

                target:
                    Number(
                        item.amount || 0
                    ) *
                    1.25,
            })
        );


    return (

        <AnalyticsCard
            title="Collection Performance"
            subtitle="Actual collection versus benchmark"
        >

            <div className="h-[340px]">

                {chartData.length === 0 ? (

                    <EmptyState />

                ) : (

                    <ResponsiveContainer
                        width="100%"
                        height="100%"
                    >

                        <ComposedChart
                            data={
                                chartData
                            }
                            onClick={
                                onClick
                            }
                        >

                            <CartesianGrid
                                strokeDasharray="3 3"
                                vertical={false}
                                stroke="#E5E7EB"
                            />

                            <XAxis
                                dataKey="label"
                                axisLine={false}
                                tickLine={false}
                                tick={{
                                    fontSize: 11,
                                }}
                            />

                            <YAxis
                                tickFormatter={
                                    (value) =>
                                        `₹${(
                                            value /
                                            1000
                                        ).toFixed(0)}K`
                                }
                                axisLine={false}
                                tickLine={false}
                                tick={{
                                    fontSize: 11,
                                }}
                            />

                            <Tooltip
                                formatter={(
                                    value
                                ) =>
                                    formatCurrency(
                                        value
                                    )
                                }
                            />

                            <Legend />

                            <Bar
                                dataKey="amount"
                                fill={
                                    CHART_COLORS.green
                                }
                                radius={[
                                    6,
                                    6,
                                    0,
                                    0,
                                ]}
                                name="Collection"
                            />

                            <Line
                                type="monotone"
                                dataKey="target"
                                stroke={
                                    CHART_COLORS.indigo
                                }
                                strokeWidth={3}
                                dot={false}
                                name="Benchmark"
                            />

                        </ComposedChart>

                    </ResponsiveContainer>

                )}

            </div>

        </AnalyticsCard>
    );
};


/*
|--------------------------------------------------------------------------
| ATTENDANCE
|--------------------------------------------------------------------------
*/

const AttendanceComplianceWidget = ({
    data = {},
    compliance = {},
    onClick,
}) => {

    const chartData = [

        {
            name: "Present",
            value:
                data.present || 0,
            color:
                CHART_COLORS.green,
        },

        {
            name: "Late",
            value:
                data.late || 0,
            color:
                CHART_COLORS.orange,
        },

        {
            name: "Absent",
            value:
                data.absent || 0,
            color:
                CHART_COLORS.red,
        },

    ];


    return (

        <AnalyticsCard
            title="Attendance & Compliance"
            subtitle="Field team availability and control"
        >

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-1">

                <div className="h-[220px]">

                    <ResponsiveContainer
                        width="100%"
                        height="100%"
                    >

                        <PieChart>

                            <Pie
                                data={
                                    chartData
                                }
                                dataKey="value"
                                nameKey="name"
                                innerRadius={60}
                                outerRadius={82}
                                paddingAngle={3}
                            >

                                {chartData.map(
                                    (
                                        entry,
                                        index
                                    ) => (

                                        <Cell
                                            key={
                                                index
                                            }
                                            fill={
                                                entry.color
                                            }
                                        />

                                    )
                                )}

                            </Pie>

                            <Tooltip />

                        </PieChart>

                    </ResponsiveContainer>

                </div>


                <div className="space-y-3">

                    <ComplianceRow
                        label="Attendance"
                        value={
                            compliance.attendance ||
                            0
                        }
                    />

                    <ComplianceRow
                        label="GPS Check-In"
                        value={
                            compliance.gps?.checkIn ||
                            0
                        }
                    />

                    <ComplianceRow
                        label="GPS Check-Out"
                        value={
                            compliance.gps?.checkOut ||
                            0
                        }
                    />

                    <ComplianceRow
                        label="Journey"
                        value={
                            compliance.journey ||
                            0
                        }
                    />

                </div>

            </div>

            <button
                type="button"
                onClick={
                    onClick
                }
                className="
                    mt-5
                    w-full
                    rounded-lg
                    bg-gray-50
                    py-3
                    text-sm
                    font-semibold
                    text-brand-500
                    transition
                    hover:bg-brand-50
                "
            >
                Open Attendance Analytics →
            </button>

        </AnalyticsCard>
    );
};


/*
|--------------------------------------------------------------------------
| VISIT STATUS
|--------------------------------------------------------------------------
*/

const VisitStatusWidget = ({
    data = {},
    onClick,
}) => {

    const statuses = [

        {
            name: "Planned",
            value:
                data.planned || 0,
            color:
                CHART_COLORS.indigo,
        },

        {
            name: "Started",
            value:
                data.started || 0,
            color:
                CHART_COLORS.blue,
        },

        {
            name: "Submitted",
            value:
                data.submitted || 0,
            color:
                CHART_COLORS.orange,
        },

        {
            name: "Approved",
            value:
                data.approved || 0,
            color:
                CHART_COLORS.green,
        },

        {
            name: "Rejected",
            value:
                data.rejected || 0,
            color:
                CHART_COLORS.red,
        },

    ];


    return (

        <AnalyticsCard
            title="Visit Pipeline"
            subtitle="Status distribution"
        >

            <div className="space-y-4">

                {statuses.map(
                    (item) => {

                        const total =
                            data.total ||
                            1;

                        const percentage =
                            Math.round(
                                (
                                    item.value /
                                    total
                                ) *
                                100
                            );


                        return (

                            <div
                                key={
                                    item.name
                                }
                            >

                                <div className="mb-1 flex items-center justify-between">

                                    <span className="text-sm font-medium text-gray-600">
                                        {
                                            item.name
                                        }
                                    </span>

                                    <span className="text-sm font-bold text-navy-700">
                                        {
                                            item.value
                                        }
                                    </span>

                                </div>


                                <div
                                    className="
                                        h-2
                                        overflow-hidden
                                        rounded-full
                                        bg-gray-100
                                    "
                                >

                                    <div
                                        className="h-full rounded-full"
                                        style={{
                                            width:
                                                `${percentage}%`,
                                            backgroundColor:
                                                item.color,
                                        }}
                                    />

                                </div>

                            </div>

                        );
                    }
                )}

            </div>


            <button
                type="button"
                onClick={
                    onClick
                }
                className="
                    mt-5
                    w-full
                    rounded-lg
                    bg-gray-50
                    py-3
                    text-sm
                    font-semibold
                    text-brand-500
                    hover:bg-brand-50
                "
            >
                Open Visit Analytics →
            </button>

        </AnalyticsCard>
    );
};


/*
|--------------------------------------------------------------------------
| SALES RANKING
|--------------------------------------------------------------------------
*/

const SalesRankingChart = ({
    data = [],
    onClick,
}) => {

    const chartData =
        data
            .slice(
                0,
                8
            )
            .map(
                (item) => ({
                    name:
                        item.name,

                    collection:
                        Number(
                            item.collectionAmount ||
                            0
                        ),

                    visits:
                        Number(
                            item.visits ||
                            0
                        ),
                })
            );


    return (

        <AnalyticsCard
            title="Sales Person Performance"
            subtitle="Ranked by collection value"
        >

            <div className="h-[350px]">

                {chartData.length === 0 ? (

                    <EmptyState />

                ) : (

                    <ResponsiveContainer
                        width="100%"
                        height="100%"
                    >

                        <BarChart
                            data={
                                chartData
                            }
                            layout="vertical"
                            margin={{
                                top: 5,
                                right: 20,
                                left: 20,
                                bottom: 5,
                            }}
                            onClick={
                                onClick
                            }
                        >

                            <CartesianGrid
                                strokeDasharray="3 3"
                                horizontal={false}
                                stroke="#E5E7EB"
                            />

                            <XAxis
                                type="number"
                                tickFormatter={
                                    (value) =>
                                        `₹${(
                                            value /
                                            1000
                                        ).toFixed(0)}K`
                                }
                                axisLine={false}
                                tickLine={false}
                            />

                            <YAxis
                                dataKey="name"
                                type="category"
                                width={110}
                                axisLine={false}
                                tickLine={false}
                                tick={{
                                    fontSize: 11,
                                }}
                            />

                            <Tooltip
                                formatter={(
                                    value
                                ) =>
                                    formatCurrency(
                                        value
                                    )
                                }
                            />

                            <Bar
                                dataKey="collection"
                                fill={
                                    CHART_COLORS.indigo
                                }
                                radius={[
                                    0,
                                    6,
                                    6,
                                    0,
                                ]}
                                name="Collection"
                            />

                        </BarChart>

                    </ResponsiveContainer>

                )}

            </div>

        </AnalyticsCard>
    );
};


/*
|--------------------------------------------------------------------------
| APPRAISALS
|--------------------------------------------------------------------------
*/

const AppraisalWidget = ({
    data = {},
    onClick,
}) => {

    const chartData = [

        {
            name: "Draft",
            value:
                data.pending || 0,
            color:
                CHART_COLORS.gray,
        },

        {
            name: "Approved",
            value:
                data.approved || 0,
            color:
                CHART_COLORS.green,
        },

        {
            name: "Rejected",
            value:
                data.rejected || 0,
            color:
                CHART_COLORS.red,
        },

    ];


    return (

        <AnalyticsCard
            title="Appraisal Pipeline"
            subtitle="Opportunity and expected orders"
        >

            <div className="h-[210px]">

                <ResponsiveContainer
                    width="100%"
                    height="100%"
                >

                    <PieChart>

                        <Pie
                            data={
                                chartData
                            }
                            dataKey="value"
                            nameKey="name"
                            innerRadius={48}
                            outerRadius={72}
                            paddingAngle={3}
                        >

                            {chartData.map(
                                (
                                    item,
                                    index
                                ) => (

                                    <Cell
                                        key={
                                            index
                                        }
                                        fill={
                                            item.color
                                        }
                                    />

                                )
                            )}

                        </Pie>

                        <Tooltip />

                    </PieChart>

                </ResponsiveContainer>

            </div>


            <div className="rounded-xl bg-purple-50 p-4">

                <p
                    className="
                        text-[11px]
                        font-bold
                        uppercase
                        tracking-wider
                        text-purple-500
                    "
                >
                    Expected Order
                </p>

                <p
                    className="
                        mt-1
                        text-2xl
                        font-bold
                        text-purple-700
                    "
                >
                    {
                        formatCurrency(
                            data.expectedOrder
                        )
                    }
                </p>

            </div>


            <button
                type="button"
                onClick={
                    onClick
                }
                className="
                    mt-4
                    w-full
                    rounded-lg
                    bg-gray-50
                    py-3
                    text-sm
                    font-semibold
                    text-brand-500
                    hover:bg-brand-50
                "
            >
                Open Appraisal Analytics →
            </button>

        </AnalyticsCard>
    );
};


/*
|--------------------------------------------------------------------------
| SAMPLE
|--------------------------------------------------------------------------
*/

const SampleWidget = ({
    data = {},
    onClick,
}) => {

    return (

        <AnalyticsCard
            title="Sample Conversion"
            subtitle="Issued to converted sample flow"
        >

            <div
                className="
                    flex
                    items-center
                    justify-center
                "
            >

                <div className="relative">

                    <div
                        className="
                            h-44
                            w-44
                            rounded-full
                            border-[18px]
                            border-purple-100
                        "
                    />

                    <div
                        className="
                            absolute
                            inset-0
                            flex
                            flex-col
                            items-center
                            justify-center
                        "
                    >

                        <p
                            className="
                                text-3xl
                                font-bold
                                text-purple-700
                            "
                        >
                            {
                                data.conversionRate ||
                                0
                            }%
                        </p>

                        <p
                            className="
                                text-xs
                                font-medium
                                text-gray-500
                            "
                        >
                            Conversion
                        </p>

                    </div>

                </div>

            </div>


            <div
                className="
                    mt-5
                    grid
                    grid-cols-3
                    gap-3
                "
            >

                <SmallMetric
                    label="Issued"
                    value={
                        data.issued ||
                        0
                    }
                />

                <SmallMetric
                    label="Returned"
                    value={
                        data.returned ||
                        0
                    }
                />

                <SmallMetric
                    label="Converted"
                    value={
                        data.converted ||
                        0
                    }
                />

            </div>


            <button
                type="button"
                onClick={
                    onClick
                }
                className="
                    mt-4
                    w-full
                    rounded-lg
                    bg-gray-50
                    py-3
                    text-sm
                    font-semibold
                    text-brand-500
                    hover:bg-brand-50
                "
            >
                Open Sample Analytics →
            </button>

        </AnalyticsCard>
    );
};


/*
|--------------------------------------------------------------------------
| MANAGEMENT ATTENTION
|--------------------------------------------------------------------------
*/

const ManagementAttention = ({
    data = [],
    onClick,
}) => {

    return (

        <AnalyticsCard
            title="Management Attention"
            subtitle="Items requiring action"
        >

            {data.length === 0 ? (

                <div
                    className="
                        rounded-xl
                        bg-green-50
                        p-5
                        text-sm
                        font-semibold
                        text-green-700
                    "
                >
                    Everything is under control.
                </div>

            ) : (

                <div className="space-y-3">

                    {data
                        .slice(0, 5)
                        .map(
                            (
                                alert,
                                index
                            ) => (

                                <button
                                    type="button"
                                    key={
                                        index
                                    }
                                    onClick={
                                        onClick
                                    }
                                    className="
                                        flex
                                        w-full
                                        items-start
                                        gap-3
                                        rounded-xl
                                        border
                                        border-gray-100
                                        p-3
                                        text-left
                                        transition
                                        hover:bg-gray-50
                                    "
                                >

                                    <span
                                        className={`
                                            mt-1.5
                                            h-2.5
                                            w-2.5
                                            rounded-full
                                            ${
                                                alert.severity ===
                                                "warning"
                                                    ? "bg-orange-500"
                                                    : "bg-blue-500"
                                            }
                                        `}
                                    />

                                    <div>

                                        <p
                                            className="
                                                text-sm
                                                font-semibold
                                                text-navy-700
                                            "
                                        >
                                            {
                                                alert.title
                                            }
                                        </p>

                                        <p
                                            className="
                                                mt-1
                                                text-xs
                                                leading-5
                                                text-gray-500
                                            "
                                        >
                                            {
                                                alert.message
                                            }
                                        </p>

                                    </div>

                                </button>

                            )
                        )}

                </div>

            )}


            <button
                type="button"
                onClick={
                    onClick
                }
                className="
                    mt-4
                    w-full
                    rounded-lg
                    bg-gray-50
                    py-3
                    text-sm
                    font-semibold
                    text-brand-500
                    hover:bg-brand-50
                "
            >
                Open Approval Queue →
            </button>

        </AnalyticsCard>
    );
};


/*
|--------------------------------------------------------------------------
| ANALYTICS: SALES
|--------------------------------------------------------------------------
*/

const SalesAnalytics = ({
    data,
}) => {

    const sales =
        data?.salesPerformance ||
        [];


    return (

        <div className="space-y-5">

            <SalesRankingChart
                data={
                    sales
                }
            />

            <AnalyticsCard
                title="Sales Person Details"
                subtitle="Detailed productivity metrics"
            >

                <div className="overflow-x-auto">

                    <table
                        className="
                            w-full
                            min-w-[900px]
                        "
                    >

                        <thead>

                            <tr
                                className="
                                    border-b
                                    border-gray-200
                                "
                            >

                                <TableHead>
                                    Rank
                                </TableHead>

                                <TableHead>
                                    Sales Person
                                </TableHead>

                                <TableHead>
                                    Visits
                                </TableHead>

                                <TableHead>
                                    Approved
                                </TableHead>

                                <TableHead>
                                    Completion
                                </TableHead>

                                <TableHead>
                                    Collections
                                </TableHead>

                                <TableHead>
                                    Expected Order
                                </TableHead>

                                <TableHead>
                                    Attendance
                                </TableHead>

                            </tr>

                        </thead>


                        <tbody>

                            {sales.map(
                                (
                                    item
                                ) => (

                                    <tr
                                        key={
                                            item.id
                                        }
                                        className="
                                            border-b
                                            border-gray-100
                                        "
                                    >

                                        <TableCell>
                                            #
                                            {
                                                item.rank
                                            }
                                        </TableCell>

                                        <TableCell>
                                            <div>
                                                <p
                                                    className="
                                                        font-semibold
                                                        text-navy-700
                                                    "
                                                >
                                                    {
                                                        item.name
                                                    }
                                                </p>

                                                <p
                                                    className="
                                                        text-xs
                                                        text-gray-500
                                                    "
                                                >
                                                    {
                                                        item.employeeCode
                                                    }
                                                </p>
                                            </div>
                                        </TableCell>

                                        <TableCell>
                                            {
                                                item.visits
                                            }
                                        </TableCell>

                                        <TableCell>
                                            {
                                                item.approvedVisits
                                            }
                                        </TableCell>

                                        <TableCell>
                                            {
                                                item.visitCompletion
                                            }%
                                        </TableCell>

                                        <TableCell>
                                            {
                                                formatCurrency(
                                                    item.collectionAmount
                                                )
                                            }
                                        </TableCell>

                                        <TableCell>
                                            {
                                                formatCurrency(
                                                    item.expectedOrder
                                                )
                                            }
                                        </TableCell>

                                        <TableCell>
                                            {
                                                item.attendance
                                            }
                                        </TableCell>

                                    </tr>

                                )
                            )}

                        </tbody>

                    </table>

                </div>

            </AnalyticsCard>

        </div>
    );
};


/*
|--------------------------------------------------------------------------
| ANALYTICS: VISITS
|--------------------------------------------------------------------------
*/

const VisitAnalytics = ({
    data,
}) => {

    const visits =
        data?.visits ||
        {};


    return (

        <div className="space-y-5">

            <div
                className="
                    grid
                    grid-cols-1
                    gap-4
                    md:grid-cols-4
                "
            >

                <StatCard
                    title="Total Visits"
                    value={
                        visits.total ||
                        0
                    }
                    color="blue"
                />

                <StatCard
                    title="Approved"
                    value={
                        visits.approved ||
                        0
                    }
                    color="green"
                />

                <StatCard
                    title="Submitted"
                    value={
                        visits.submitted ||
                        0
                    }
                    color="orange"
                />

                <StatCard
                    title="Completion"
                    value={`${visits.completionRate || 0}%`}
                    color="indigo"
                />

            </div>


            <SalesVisitTrend
                data={
                    visits.trend ||
                    []
                }
            />


            <AnalyticsCard
                title="Visit Pipeline"
                subtitle="Execution status across the selected period"
            >

                <div
                    className="
                        grid
                        grid-cols-1
                        gap-4
                        md:grid-cols-5
                    "
                >

                    <PipelineBox
                        title="Planned"
                        value={
                            visits.planned ||
                            0
                        }
                        color="indigo"
                    />

                    <PipelineBox
                        title="Started"
                        value={
                            visits.started ||
                            0
                        }
                        color="blue"
                    />

                    <PipelineBox
                        title="Submitted"
                        value={
                            visits.submitted ||
                            0
                        }
                        color="orange"
                    />

                    <PipelineBox
                        title="Approved"
                        value={
                            visits.approved ||
                            0
                        }
                        color="green"
                    />

                    <PipelineBox
                        title="Rejected"
                        value={
                            visits.rejected ||
                            0
                        }
                        color="red"
                    />

                </div>

            </AnalyticsCard>

        </div>
    );
};


/*
|--------------------------------------------------------------------------
| ANALYTICS: COLLECTIONS
|--------------------------------------------------------------------------
*/

const CollectionAnalytics = ({
    data,
}) => {

    const collections =
        data?.collections ||
        {};


    return (

        <div className="space-y-5">

            <div
                className="
                    grid
                    grid-cols-1
                    gap-4
                    md:grid-cols-4
                "
            >

                <StatCard
                    title="Period Collection"
                    value={
                        formatCurrency(
                            collections.amount
                        )
                    }
                    color="green"
                />

                <StatCard
                    title="Today's Collection"
                    value={
                        formatCurrency(
                            collections.todayAmount
                        )
                    }
                    color="blue"
                />

                <StatCard
                    title="Pending"
                    value={
                        formatCurrency(
                            collections.pendingAmount
                        )
                    }
                    color="orange"
                />

                <StatCard
                    title="Transactions"
                    value={
                        collections.total ||
                        0
                    }
                    color="purple"
                />

            </div>


            <CollectionPerformanceChart
                data={
                    collections.trend ||
                    []
                }
            />


            <AnalyticsCard
                title="Collection Trend Detail"
                subtitle="Daily collection performance"
            >

                <div className="overflow-x-auto">

                    <table
                        className="
                            w-full
                            min-w-[600px]
                        "
                    >

                        <thead>

                            <tr
                                className="
                                    border-b
                                    border-gray-200
                                "
                            >

                                <TableHead>
                                    Date
                                </TableHead>

                                <TableHead>
                                    Collection
                                </TableHead>

                                <TableHead>
                                    Benchmark
                                </TableHead>

                                <TableHead>
                                    Achievement
                                </TableHead>

                            </tr>

                        </thead>


                        <tbody>

                            {(
                                collections.trend ||
                                []
                            ).map(
                                (
                                    item
                                ) => {

                                    const amount =
                                        Number(
                                            item.amount ||
                                            0
                                        );

                                    const target =
                                        amount *
                                        1.25;

                                    const achievement =
                                        target > 0
                                            ? (
                                                amount /
                                                target
                                            ) *
                                            100
                                            : 0;


                                    return (

                                        <tr
                                            key={
                                                item.date
                                            }
                                            className="
                                                border-b
                                                border-gray-100
                                            "
                                        >

                                            <TableCell>
                                                {
                                                    item.label
                                                }
                                            </TableCell>

                                            <TableCell>
                                                {
                                                    formatCurrency(
                                                        amount
                                                    )
                                                }
                                            </TableCell>

                                            <TableCell>
                                                {
                                                    formatCurrency(
                                                        target
                                                    )
                                                }
                                            </TableCell>

                                            <TableCell>
                                                <span
                                                    className="
                                                        rounded-full
                                                        bg-green-50
                                                        px-3
                                                        py-1
                                                        text-xs
                                                        font-semibold
                                                        text-green-700
                                                    "
                                                >
                                                    {
                                                        achievement.toFixed(
                                                            0
                                                        )
                                                    }%
                                                </span>
                                            </TableCell>

                                        </tr>

                                    );
                                }
                            )}

                        </tbody>

                    </table>

                </div>

            </AnalyticsCard>

        </div>
    );
};


/*
|--------------------------------------------------------------------------
| ANALYTICS: ATTENDANCE
|--------------------------------------------------------------------------
*/

const AttendanceAnalytics = ({
    data,
}) => {

    const attendance =
        data?.attendance ||
        {};

    const compliance =
        data?.compliance ||
        {};


    return (

        <div className="space-y-5">

            <div
                className="
                    grid
                    grid-cols-1
                    gap-4
                    md:grid-cols-4
                "
            >

                <StatCard
                    title="Present"
                    value={
                        attendance.present ||
                        0
                    }
                    color="green"
                />

                <StatCard
                    title="Late"
                    value={
                        attendance.late ||
                        0
                    }
                    color="orange"
                />

                <StatCard
                    title="Absent"
                    value={
                        attendance.absent ||
                        0
                    }
                    color="red"
                />

                <StatCard
                    title="Attendance"
                    value={`${attendance.compliance || 0}%`}
                    color="teal"
                />

            </div>


            <div
                className="
                    grid
                    grid-cols-1
                    gap-5
                    xl:grid-cols-2
                "
            >

                <AttendanceComplianceWidget
                    data={
                        attendance
                    }
                    compliance={
                        compliance
                    }
                />


                <AnalyticsCard
                    title="Compliance Metrics"
                    subtitle="Operational control indicators"
                >

                    <div className="space-y-5">

                        <ComplianceRow
                            label="Attendance"
                            value={
                                compliance.attendance ||
                                0
                            }
                        />

                        <ComplianceRow
                            label="GPS Check-In"
                            value={
                                compliance.gps?.checkIn ||
                                0
                            }
                        />

                        <ComplianceRow
                            label="GPS Check-Out"
                            value={
                                compliance.gps?.checkOut ||
                                0
                            }
                        />

                        <ComplianceRow
                            label="Journey Compliance"
                            value={
                                compliance.journey ||
                                0
                            }
                        />

                    </div>

                </AnalyticsCard>

            </div>


            <div
                className="
                    rounded-2xl
                    border
                    border-gray-200
                    bg-white
                    p-6
                    shadow-sm
                "
            >

                <TodayAttendanceCard />

            </div>

        </div>
    );
};


/*
|--------------------------------------------------------------------------
| ANALYTICS: APPRAISALS
|--------------------------------------------------------------------------
*/

const AppraisalAnalytics = ({
    data,
}) => {

    const appraisals =
        data?.appraisals ||
        {};


    return (

        <div className="space-y-5">

            <div
                className="
                    grid
                    grid-cols-1
                    gap-4
                    md:grid-cols-4
                "
            >

                <StatCard
                    title="Total Appraisals"
                    value={
                        appraisals.total ||
                        0
                    }
                    color="purple"
                />

                <StatCard
                    title="Approved"
                    value={
                        appraisals.approved ||
                        0
                    }
                    color="green"
                />

                <StatCard
                    title="Pending"
                    value={
                        appraisals.pending ||
                        0
                    }
                    color="orange"
                />

                <StatCard
                    title="Expected Order"
                    value={
                        formatCurrency(
                            appraisals.expectedOrder
                        )
                    }
                    color="indigo"
                />

            </div>


            <AnalyticsCard
                title="Appraisal Pipeline"
                subtitle="Opportunity stage"
            >

                <div
                    className="
                        grid
                        grid-cols-1
                        gap-4
                        md:grid-cols-4
                    "
                >

                    <PipelineBox
                        title="Pending"
                        value={
                            appraisals.pending ||
                            0
                        }
                        color="orange"
                    />

                    <PipelineBox
                        title="Approved"
                        value={
                            appraisals.approved ||
                            0
                        }
                        color="green"
                    />

                    <PipelineBox
                        title="Rejected"
                        value={
                            appraisals.rejected ||
                            0
                        }
                        color="red"
                    />

                    <PipelineBox
                        title="Approval Rate"
                        value={`${appraisals.approvalRate || 0}%`}
                        color="purple"
                    />

                </div>

            </AnalyticsCard>

        </div>
    );
};


/*
|--------------------------------------------------------------------------
| ANALYTICS: SAMPLES
|--------------------------------------------------------------------------
*/

const SampleAnalytics = ({
    data,
}) => {

    const samples =
        data?.samples ||
        {};


    return (

        <div className="space-y-5">

            <div
                className="
                    grid
                    grid-cols-1
                    gap-4
                    md:grid-cols-4
                "
            >

                <StatCard
                    title="Issued"
                    value={
                        samples.issued ||
                        0
                    }
                    color="indigo"
                />

                <StatCard
                    title="Returned"
                    value={
                        samples.returned ||
                        0
                    }
                    color="orange"
                />

                <StatCard
                    title="Converted"
                    value={
                        samples.converted ||
                        0
                    }
                    color="green"
                />

                <StatCard
                    title="Conversion"
                    value={`${samples.conversionRate || 0}%`}
                    color="purple"
                />

            </div>


            <AnalyticsCard
                title="Sample Conversion Funnel"
                subtitle="Sample movement across the sales cycle"
            >

                <div className="space-y-5">

                    <PipelineBox
                        title="Samples Issued"
                        value={
                            samples.issued ||
                            0
                        }
                        color="indigo"
                        wide
                    />

                    <PipelineBox
                        title="Samples Returned"
                        value={
                            samples.returned ||
                            0
                        }
                        color="orange"
                        wide
                    />

                    <PipelineBox
                        title="Samples Converted"
                        value={
                            samples.converted ||
                            0
                        }
                        color="green"
                        wide
                    />

                </div>

            </AnalyticsCard>

        </div>
    );
};


/*
|--------------------------------------------------------------------------
| ANALYTICS: APPROVALS
|--------------------------------------------------------------------------
*/

const ApprovalAnalytics = ({
    data,
}) => {

    const queue =
        data?.approvalQueue ||
        {};


    return (

        <div className="space-y-5">

            <div
                className="
                    grid
                    grid-cols-1
                    gap-4
                    md:grid-cols-3
                "
            >

                <StatCard
                    title="Total Pending"
                    value={
                        queue.total ||
                        0
                    }
                    color="orange"
                />

                <StatCard
                    title="Visits"
                    value={
                        queue.visits ||
                        0
                    }
                    color="blue"
                />

                <StatCard
                    title="Collections"
                    value={
                        queue.collections ||
                        0
                    }
                    color="green"
                />

            </div>


            <AnalyticsCard
                title="Approval Queue"
                subtitle="Items waiting for management action"
            >

                <div className="space-y-4">

                    <ApprovalRow
                        label="Visits"
                        value={
                            queue.visits
                        }
                    />

                    <ApprovalRow
                        label="Expenses"
                        value={
                            queue.expenses
                        }
                    />

                    <ApprovalRow
                        label="TA / DA"
                        value={
                            queue.taDa
                        }
                    />

                    <ApprovalRow
                        label="Collections"
                        value={
                            queue.collections
                        }
                    />

                    <ApprovalRow
                        label="Appraisals"
                        value={
                            queue.appraisals
                        }

                    />

                </div>

            </AnalyticsCard>

        </div>
    );
};


/*
|--------------------------------------------------------------------------
| SMALL COMPONENTS
|--------------------------------------------------------------------------
*/

const ComplianceRow = ({
    label,
    value = 0,
}) => {

    return (

        <div>

            <div
                className="
                    mb-2
                    flex
                    items-center
                    justify-between
                "
            >

                <span
                    className="
                        text-sm
                        font-medium
                        text-gray-600
                    "
                >
                    {label}
                </span>

                <span
                    className="
                        text-sm
                        font-bold
                        text-navy-700
                    "
                >
                    {value}%
                </span>

            </div>


            <div
                className="
                    h-2
                    overflow-hidden
                    rounded-full
                    bg-gray-100
                "
            >

                <div
                    className="
                        h-full
                        rounded-full
                        bg-indigo-500
                    "
                    style={{
                        width:
                            `${Math.min(
                                Number(
                                    value || 0
                                ),
                                100
                            )}%`,
                    }}
                />

            </div>

        </div>
    );
};


const SmallMetric = ({
    label,
    value,
}) => {

    return (

        <div
            className="
                rounded-xl
                bg-gray-50
                p-3
                text-center
            "
        >

            <p
                className="
                    text-[11px]
                    font-semibold
                    uppercase
                    text-gray-400
                "
            >
                {label}
            </p>

            <p
                className="
                    mt-1
                    text-lg
                    font-bold
                    text-navy-700
                "
            >
                {value}
            </p>

        </div>
    );
};


const StatCard = ({
    title,
    value,
    color = "indigo",
}) => {

    const colors = {
        indigo:
            "bg-indigo-50 text-indigo-700",

        blue:
            "bg-blue-50 text-blue-700",

        green:
            "bg-green-50 text-green-700",

        orange:
            "bg-orange-50 text-orange-700",

        purple:
            "bg-purple-50 text-purple-700",

        teal:
            "bg-teal-50 text-teal-700",

        red:
            "bg-red-50 text-red-700",
    };


    return (

        <div
            className={`
                rounded-2xl
                border
                border-gray-200
                p-5
                shadow-sm
                ${colors[color]}
            `}
        >

            <p
                className="
                    text-xs
                    font-bold
                    uppercase
                    tracking-wider
                    opacity-70
                "
            >
                {title}
            </p>

            <p
                className="
                    mt-3
                    text-3xl
                    font-bold
                "
            >
                {value}
            </p>

        </div>
    );
};


const PipelineBox = ({
    title,
    value,
    color = "indigo",
    wide = false,
}) => {

    const colors = {
        indigo:
            "bg-indigo-50 text-indigo-700 border-indigo-100",

        blue:
            "bg-blue-50 text-blue-700 border-blue-100",

        green:
            "bg-green-50 text-green-700 border-green-100",

        orange:
            "bg-orange-50 text-orange-700 border-orange-100",

        purple:
            "bg-purple-50 text-purple-700 border-purple-100",

        red:
            "bg-red-50 text-red-700 border-red-100",
    };


    return (

        <div
            className={`
                rounded-xl
                border
                p-5
                ${colors[color]}
                ${
                    wide
                        ? "w-full"
                        : ""
                }
            `}
        >

            <p
                className="
                    text-xs
                    font-bold
                    uppercase
                    tracking-wider
                    opacity-70
                "
            >
                {title}
            </p>

            <p
                className="
                    mt-2
                    text-2xl
                    font-bold
                "
            >
                {value}
            </p>

        </div>
    );
};


const ApprovalRow = ({
    label,
    value = 0,
}) => {

    return (

        <div
            className="
                flex
                items-center
                justify-between
                rounded-xl
                border
                border-gray-100
                bg-gray-50
                p-4
            "
        >

            <span
                className="
                    text-sm
                    font-semibold
                    text-gray-600
                "
            >
                {label}
            </span>

            <span
                className="
                    rounded-full
                    bg-orange-50
                    px-3
                    py-1
                    text-sm
                    font-bold
                    text-orange-700
                "
            >
                {value || 0}
            </span>

        </div>
    );
};


const TableHead = ({
    children,
}) => (

    <th
        className="
            px-4
            py-3
            text-left
            text-[11px]
            font-bold
            uppercase
            tracking-wider
            text-gray-400
        "
    >
        {children}
    </th>
);


const TableCell = ({
    children,
}) => (

    <td
        className="
            px-4
            py-4
            text-sm
            text-gray-700
        "
    >
        {children}
    </td>
);


const EmptyState = () => (

    <div
        className="
            flex
            h-full
            items-center
            justify-center
            rounded-xl
            bg-gray-50
            text-sm
            text-gray-400
        "
    >
        No analytics data available
        for the selected period.
    </div>
);


export default Dashboard;